/**
 * Comments sidebar: threads, replies, edit and delete.
 *
 * These are real PDF annotations, so a thread started here reads as a thread
 * in Acrobat or Edge. Comments the document already carried are shown too,
 * marked as external — they can be replied to but Odie does not rewrite them,
 * because another tool made them and may depend on details we would not keep.
 */

import { useState } from 'react'
import type { CommentThread } from '../api'

interface Props {
  open: boolean
  threads: CommentThread[]
  activeId: string | null
  onClose: () => void
  onSelect: (id: string, pageIndex: number) => void
  onReply: (id: string, text: string) => void
  onEdit: (id: string, text: string) => void
  onDelete: (id: string) => void
}

const KIND_LABEL: Record<string, string> = {
  note: 'Note',
  highlight: 'Highlight',
  freetext: 'Text box',
}

function when(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.valueOf())) return ''
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function CommentsPanel({
  open,
  threads,
  activeId,
  onClose,
  onSelect,
  onReply,
  onEdit,
  onDelete,
}: Props) {
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState<string | null>(null)

  if (!open) return null

  const total = threads.reduce((sum, t) => sum + 1 + t.replies.length, 0)

  return (
    <aside className="comments" aria-label="Comments">
      <div className="comments__header">
        <span>
          Comments{total ? ` (${total})` : ''}
        </span>
        <button className="iconbtn" onClick={onClose} title="Hide comments">
          ✕
        </button>
      </div>

      {threads.length === 0 && (
        <p className="comments__empty">
          No comments yet. Use <strong>Note</strong> or <strong>Highlight</strong>{' '}
          on the Review tab, then click the page.
        </p>
      )}

      <div className="comments__list">
        {threads.map(({ comment, replies }) => (
          <div
            key={comment.id}
            className="thread"
            data-active={comment.id === activeId}
            onClick={() => onSelect(comment.id, comment.pageIndex)}
          >
            <div className="thread__head">
              <span
                className="thread__swatch"
                style={{
                  background: `rgb(${comment.color
                    .map((c) => Math.round(c * 255))
                    .join(',')})`,
                }}
                aria-hidden="true"
              />
              <span className="thread__kind">
                {KIND_LABEL[comment.kind] ?? comment.kind}
              </span>
              <span className="thread__page">p{comment.pageIndex + 1}</span>
              {comment.external && (
                <span className="thread__badge" title="Made in another tool">
                  external
                </span>
              )}
            </div>

            <div className="thread__meta">
              {comment.author || 'Unknown'}
              {when(comment.created) && ` · ${when(comment.created)}`}
            </div>

            {editing === comment.id ? (
              <div className="thread__compose" onClick={(e) => e.stopPropagation()}>
                <textarea
                  autoFocus
                  defaultValue={comment.contents}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                />
                <div className="thread__actions">
                  <button
                    className="btn btn--small"
                    onClick={() => {
                      setEditing(null)
                      setDraft('')
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn--small btn--primary"
                    onClick={() => {
                      onEdit(comment.id, draft || comment.contents)
                      setEditing(null)
                      setDraft('')
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="thread__body">{comment.contents || <em>(no text)</em>}</p>
            )}

            {replies.map((reply) => (
              <div className="reply" key={reply.id}>
                <div className="thread__meta">
                  {reply.author || 'Unknown'}
                  {when(reply.created) && ` · ${when(reply.created)}`}
                </div>
                <p className="thread__body">{reply.contents}</p>
              </div>
            ))}

            {replyTo === comment.id ? (
              <div className="thread__compose" onClick={(e) => e.stopPropagation()}>
                <textarea
                  autoFocus
                  placeholder="Reply…"
                  onChange={(e) => setDraft(e.target.value)}
                  rows={3}
                />
                <div className="thread__actions">
                  <button
                    className="btn btn--small"
                    onClick={() => {
                      setReplyTo(null)
                      setDraft('')
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn--small btn--primary"
                    disabled={!draft.trim()}
                    onClick={() => {
                      onReply(comment.id, draft.trim())
                      setReplyTo(null)
                      setDraft('')
                    }}
                  >
                    Reply
                  </button>
                </div>
              </div>
            ) : (
              <div className="thread__actions" onClick={(e) => e.stopPropagation()}>
                <button
                  className="linkbtn"
                  onClick={() => {
                    setReplyTo(comment.id)
                    setDraft('')
                  }}
                >
                  Reply
                </button>
                {!comment.external && (
                  <button
                    className="linkbtn"
                    onClick={() => {
                      setEditing(comment.id)
                      setDraft(comment.contents)
                    }}
                  >
                    Edit
                  </button>
                )}
                <button
                  className="linkbtn linkbtn--danger"
                  onClick={() => onDelete(comment.id)}
                  title={
                    replies.length
                      ? `Deletes this comment and its ${replies.length} repl${
                          replies.length === 1 ? 'y' : 'ies'
                        }`
                      : 'Delete this comment'
                  }
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  )
}
